import React, { useEffect, useState } from 'react';
import { 
  Grid, 
  Typography, 
  TextField, 
  MenuItem, 
  Box,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { SubBrokerAttribution } from '../../../../types/utility.types';
import { User } from '../../../../types/user.types';
import * as userApi from '../../../../api/users';
import * as lookupApi from '../../../../api/lookups';

interface AdjustedCommissionData {
  isSubBrokerInactive: boolean;
  lockedCommissionRate: number;
  effectiveCommissionRate: number;
  adjustedOutstandingBalance: number;
  penaltyApplied: boolean;
}

interface SubBrokerSectionProps {
  clientRepresentativeId?: string;
  subBroker?: SubBrokerAttribution;
  onSubBrokerChange: (subBroker: SubBrokerAttribution | undefined) => void;
  isViewMode: boolean;
  isEditMode: boolean;
  adjustedCommissionData?: AdjustedCommissionData | null;
}

interface SubBrokerLevel {
  value: string;
  description: string;
  metadata: {
    commissionRate: number;
    brokerSplit: number;
    subBrokerSplit: number;
    level: number;
  };
}

export const SubBrokerSection: React.FC<SubBrokerSectionProps> = ({
  clientRepresentativeId,
  subBroker,
  onSubBrokerChange,
  isViewMode,
  isEditMode,
  adjustedCommissionData,
}) => {
  const [loading, setLoading] = useState(false);
  const [subBrokerUser, setSubBrokerUser] = useState<User | null>(null);
  const [subBrokerLevels, setSubBrokerLevels] = useState<SubBrokerLevel[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<SubBrokerLevel | null>(null);

  // Load sub-broker levels from lookups
  useEffect(() => {
    const loadSubBrokerLevels = async () => {
      try {
        const lookups = await lookupApi.getLookupsByType('SubBrokerLevel');
        const levels = lookups.map((lookup: any) => ({
          value: lookup.value,
          description: lookup.description,
          metadata: lookup.metadata || {
            commissionRate: 0,
            brokerSplit: 50,
            subBrokerSplit: 50,
            level: 1
          }
        }));
        setSubBrokerLevels(levels);
      } catch (error) {
        console.error('Failed to load sub-broker levels:', error);
      }
    };

    loadSubBrokerLevels();
  }, []);

  // Auto-populate sub-broker from client's representative
  useEffect(() => {
    const loadSubBrokerFromClient = async () => {
      if (!clientRepresentativeId || subBroker || isEditMode) {
        return;
      }

      setLoading(true);
      try {
        // Fetch the representative user details
        const user = await userApi.getUser(clientRepresentativeId);
        console.log('Loaded sub-broker user:', user);
        setSubBrokerUser(user);

        // Get their sub-broker level
        const userLevel = user.subBrokerLevel;
        console.log('User sub-broker level:', userLevel);
        console.log('Available levels:', subBrokerLevels);
        
        if (userLevel) {
          const level = subBrokerLevels.find(l => l.value === userLevel);
          console.log('Found matching level:', level);
          
          if (level) {
            setSelectedLevel(level);
            
            // Create sub-broker attribution
            const attribution: SubBrokerAttribution = {
              userId: user._id,
              level: level.value,
              commissionRate: level.metadata.commissionRate,
              lockedAt: new Date().toISOString()
            };
            
            console.log('Setting sub-broker attribution:', attribution);
            onSubBrokerChange(attribution);
          } else {
            console.warn('No matching level found for:', userLevel);
          }
        } else {
          console.log('User has no sub-broker level assigned');
        }
      } catch (error) {
        console.error('Failed to load sub-broker details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (subBrokerLevels.length > 0) {
      loadSubBrokerFromClient();
    }
  }, [clientRepresentativeId, subBrokerLevels, subBroker, isEditMode, onSubBrokerChange]);

  // Load existing sub-broker details if in edit/view mode
  useEffect(() => {
    const loadExistingSubBroker = async () => {
      if (!subBroker || subBrokerUser) {
        return;
      }

      setLoading(true);
      try {
        const user = await userApi.getUser(subBroker.userId);
        setSubBrokerUser(user);

        const level = subBrokerLevels.find(l => l.value === subBroker.level);
        if (level) {
          setSelectedLevel(level);
        }
      } catch (error) {
        console.error('Failed to load sub-broker user:', error);
      } finally {
        setLoading(false);
      }
    };

    if (subBrokerLevels.length > 0) {
      loadExistingSubBroker();
    }
  }, [subBroker, subBrokerLevels, subBrokerUser]);

  // Handle level change (only for new contracts)
  const handleLevelChange = (levelValue: string) => {
    const level = subBrokerLevels.find(l => l.value === levelValue);
    if (level && subBrokerUser) {
      setSelectedLevel(level);
      
      const attribution: SubBrokerAttribution = {
        userId: subBrokerUser._id,
        level: level.value,
        commissionRate: level.metadata.commissionRate,
        lockedAt: new Date().toISOString()
      };
      
      onSubBrokerChange(attribution);
    }
  };

  // Don't show section if no representative and not in edit mode with existing sub-broker
  if (!clientRepresentativeId && !subBroker) {
    return null;
  }

  // Only lock in view mode or when editing an existing contract with sub-broker
  // In create mode, allow changing the level even if auto-populated
  const isLocked = isViewMode || isEditMode;

  return (
    <Grid item xs={12}>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Sub-Broker Attribution
          {isLocked && (
            <Chip 
              label="Locked" 
              size="small" 
              color="info" 
              sx={{ ml: 2 }}
            />
          )}
        </Typography>

        {isLocked && subBroker && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Sub-broker attribution is locked at contract creation and cannot be changed.
            Locked on: {new Date(subBroker.lockedAt).toLocaleDateString()}
          </Alert>
        )}

        {/* Penalty Warning for Inactive Sub-Broker */}
        {adjustedCommissionData?.penaltyApplied && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ Sub-Broker Inactive - Commission Adjusted
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This sub-broker is currently inactive. Commission rate has been adjusted from{' '}
              <strong>{adjustedCommissionData.lockedCommissionRate}%</strong> to{' '}
              <strong>{adjustedCommissionData.effectiveCommissionRate}%</strong> for outstanding payments only.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Already paid commissions are not affected. Adjusted outstanding balance:{' '}
              <strong>£{adjustedCommissionData.adjustedOutstandingBalance.toFixed(2)}</strong>
            </Typography>
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Sub-Broker Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sub-Broker"
                value={subBrokerUser ? `${subBrokerUser.firstName} ${subBrokerUser.lastName}` : ''}
                disabled
                helperText={subBrokerUser?.email || 'Auto-populated from client representative'}
              />
            </Grid>

            {/* Sub-Broker Level */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sub-Broker Level"
                value={selectedLevel?.value || subBrokerUser?.subBrokerLevel || 'Not assigned'}
                disabled
                helperText={selectedLevel ? `Level ${selectedLevel.metadata.level} - ${selectedLevel.metadata.commissionRate}% commission rate` : 'Assigned from user profile'}
              />
            </Grid>

            {/* Commission Split Display */}
            {selectedLevel && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Commission Split"
                    value={`${selectedLevel.metadata.brokerSplit}:${selectedLevel.metadata.subBrokerSplit}`}
                    disabled
                    helperText="Broker : Sub-Broker"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sub-Broker Commission Rate"
                    value={`${selectedLevel.metadata.commissionRate}%`}
                    disabled
                    helperText="Percentage of total commission"
                  />
                </Grid>

                {/* Commission Breakdown */}
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>Commission Breakdown:</strong> Broker receives {selectedLevel.metadata.brokerSplit}%, 
                      Sub-Broker ({subBrokerUser?.firstName} {subBrokerUser?.lastName}) receives {selectedLevel.metadata.subBrokerSplit}%
                    </Typography>
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </Box>
    </Grid>
  );
};
